/** @typedef {import('headlight-configurable-application').Pict} Pict */

const MaterialsCascadePicker = require('./PictProvider-InputType-MaterialsCascadePicker.js');
const libEntityDisplayName = require('./_EntityDisplayName.js');

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'MaterialsEntityPicker',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,
};

/**
 * The base MaterialsEntitySelector on the Picker stack (Wave 10): a field that names its own Entity (Material,
 * MixDesign, Organization, Product, …) on the MaterialsCascadePicker base, which already carries the legacy base's
 * cascade, creation and save projection. This class holds only where the legacy base differed from the broker's
 * defaults for such a field: list order and search, a dotted display field's label, AllowCustomValues and the
 * placeholder. Its config-map entry (`MaterialsEntitySelector`) brings the rest of the Materials family behaviour.
 */
class PictProviderInputTypeMaterialsEntityPicker extends MaterialsCascadePicker
{
	/**
	 * @param {Object} pFable @param {Object} [pOptions] @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DEFAULT_CONFIGURATION, pOptions), pServiceHash);
		/** @type {Pict} */
		this.pict;
	}

	/**
	 * The broker's picker config, with the legacy base's list and creation behaviour.
	 * @param {Record<string, any>} pInput @param {string} pHostSelector @param {Function} fOnChange
	 * @return {Record<string, any>}
	 */
	_buildPickerConfig(pInput, pHostSelector, fOnChange)
	{
		const tmpConfig = super._buildPickerConfig(pInput, pHostSelector, fOnChange);
		if (!tmpConfig.Entity)
		{
			return tmpConfig;
		}
		const tmpPictForm = (pInput && pInput.PictForm) || {};

		// Select2Util searched and sorted by SearchFields, in order, and by Name when the descriptor named none.
		// PSP sorts on one field, so the first stands in for the list.
		if (!Array.isArray(tmpPictForm.SearchFields) || tmpPictForm.SearchFields.length === 0)
		{
			tmpConfig.SearchFields = [ 'Name' ];
		}
		const tmpSortField = String(tmpConfig.SearchFields[0]);
		if (tmpPictForm.Sort === undefined && !tmpSortField.includes('.'))
		{
			tmpConfig.Sort = tmpSortField;
		}

		// The legacy base never read AllowCustomValues, so a typed term is never committed as the ID; a
		// creation-enabled field keeps the "New <Entity>..." row the broker's custom-value row displaced.
		if (tmpPictForm.AllowCustomValues)
		{
			delete tmpConfig.OnCreate;
			if (this._isEntityCreationEnabled(pInput))
			{
				this._applyNewEntityRow(tmpConfig);
			}
		}

		if (!tmpPictForm.Placeholder)
		{
			const tmpEntityLabel = String(tmpConfig.Entity).replace(/([a-z0-9])([A-Z])/g, '$1 $2');
			tmpConfig.Placeholder = `Select ${/^[AEIOU]/i.test(tmpEntityLabel) ? 'an' : 'a'} ${tmpEntityLabel}...`;
		}
		return tmpConfig;
	}

	/**
	 * PSP reads the display field as a flat key, so a dotted one (`MixMetadata.MixName`) would label every option
	 * blank; walk it the way the legacy base and PickerDisplay do.
	 * @param {Record<string, any>} pOption - a picker option `{ Value, Text, Record[, Tag][, Tags] }`.
	 * @param {Record<string, any>} pConfig - the picker config.
	 * @return {Record<string, any>} The option, or a copy of it.
	 */
	_presentEntityOption(pOption, pConfig)
	{
		const tmpTextField = pConfig.TextField;
		if (pOption && (typeof pOption === 'object') && pOption.Record && !pConfig.TextTemplate
			&& (typeof tmpTextField === 'string') && tmpTextField.includes('.'))
		{
			const tmpText = libEntityDisplayName.resolveEntityDisplayName(pConfig.Entity, pOption.Record, { TextField: tmpTextField });
			return super._presentEntityOption(Object.assign({}, pOption, { Text: tmpText }), pConfig);
		}
		return super._presentEntityOption(pOption, pConfig);
	}
}

module.exports = PictProviderInputTypeMaterialsEntityPicker;

module.exports.default_configuration = _DEFAULT_CONFIGURATION;
